import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LOCALES, type Locale } from '@diana/shared'
import { api, ApiError } from '../../lib/api.js'
import { FieldLabel } from '../../classroom/ui.js'

/**
 * Bits shared by the content editors (Cennik, Produkty) in the classroom
 * style: the cache helpers, error wording, a labelled field, the cover-photo
 * line, drag-to-reorder and the slug generator. Anything specific to one
 * entity stays on its own page.
 */

type Entity = 'offers' | 'products'
type Row = { id: string; order: number; title: string }

/** Refresh the panel list for `entity` and the public site's content in one go. */
export function useInvalidate(entity: Entity) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['studio', entity] })
    queryClient.invalidateQueries({ queryKey: ['content'] })
  }
}

/**
 * Write a result into the cached list straight away, so the screen never
 * shows the old row while the refetch is on its way (a new position would
 * otherwise flash the first one in the editor).
 */
export function useListCache<T extends Row>(entity: Entity) {
  const queryClient = useQueryClient()
  const key = ['studio', entity]
  return {
    put: (row: T) =>
      queryClient.setQueryData<T[]>(key, (old) =>
        old ? (old.some((r) => r.id === row.id) ? old.map((r) => (r.id === row.id ? row : r)) : [...old, row]) : old,
      ),
    drop: (id: string) => queryClient.setQueryData<T[]>(key, (old) => old?.filter((r) => r.id !== id)),
    reorder: (changes: { id: string; order: number }[]) =>
      queryClient.setQueryData<T[]>(key, (old) =>
        old?.map((r) => {
          const c = changes.find((x) => x.id === r.id)
          return c ? { ...r, order: c.order } : r
        }),
      ),
  }
}

/** The order the site shows them in — the API's own sort. */
export function byOrder<T extends Row>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, 'pl'))
}

/** Every row whose place changed gets its new index as `order` (this also mends equal orders). */
export function orderChanges(ids: string[], rows: Row[]): { id: string; order: number }[] {
  return ids.flatMap((id, i) => (rows.find((r) => r.id === id)?.order === i ? [] : [{ id, order: i }]))
}

/** Save a new order: show it at once, write it one row at a time, then refetch. */
export function useReorder(entity: Entity, patch: (id: string, order: number) => Promise<unknown>) {
  const cache = useListCache(entity)
  const invalidate = useInvalidate(entity)
  return useMutation({
    mutationFn: async (changes: { id: string; order: number }[]) => {
      for (const c of changes) await patch(c.id, c.order)
    },
    onMutate: (changes) => cache.reorder(changes),
    onSettled: invalidate,
  })
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.message === 'slug_taken') return 'Taki adres (slug) już istnieje w tym języku. Zmień go.'
    if (err.message === 'validation') return 'Coś jest nie tak z polami — sprawdź je i spróbuj jeszcze raz.'
  }
  return 'Nie udało się zapisać. Spróbuj jeszcze raz.'
}

/** `?jezyk=uk` → 'uk'; anything else is Polish. */
export function asLocale(value: string | null): Locale {
  return (LOCALES as readonly string[]).includes(value ?? '') ? (value as Locale) : 'pl'
}

/** A label above a field, an optional 12px hint under it. */
export function Field({
  id,
  label,
  hint,
  className = '',
  children,
}: {
  id: string
  label: ReactNode
  hint?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {children}
      {hint && <p className="m-0 mt-1.5 text-class-label text-class-ink-faint">{hint}</p>}
    </div>
  )
}

export function Check({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-class-gloss">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 accent-[var(--color-class-sky-700)]"
      />
      {children}
    </label>
  )
}

/** The rarely touched fields, folded away so the form reads like the card on the site. */
export function More({ defaultOpen, summary, children }: { defaultOpen: boolean; summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} className="desk:col-span-2">
      <summary className="cursor-pointer list-none text-class-gloss font-semibold text-class-sky-700 hover:text-class-ink [&::-webkit-details-marker]:hidden">
        {open ? 'Mniej ustawień' : `${summary} ›`}
      </summary>
      <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-5 desk:grid-cols-2">{children}</div>
    </details>
  )
}

/* ── cover photo ───────────────────────────────────────────────────────── */

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024

/** „Zdjęcie na karcie: brak · Dodaj zdjęcie” — the photo saves with the rest of the form. */
export function CoverLine({
  coverUrl,
  alt,
  onChange,
}: {
  coverUrl: string | null
  alt: string
  onChange: (media: { id: string; url: string } | null) => void
}) {
  const [error, setError] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: (file: File) => api.studio.uploadMedia(file, alt),
    onSuccess: (media) => {
      setError(null)
      onChange({ id: media.id, url: media.url })
    },
    onError: () => setError('Nie udało się wgrać zdjęcia. JPG, PNG lub WebP, do 8 MB.'),
  })

  function pick(file: File | undefined) {
    if (!file) return
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('To zdjęcie jest za duże — maksymalnie 8 MB.')
      return
    }
    upload.mutate(file)
  }

  return (
    <div className="flex items-center gap-4">
      {coverUrl && (
        <img
          src={coverUrl}
          alt=""
          className="h-14 w-[88px] shrink-0 rounded-class-sm object-cover shadow-[inset_0_0_0_1px_var(--color-class-hairline)]"
        />
      )}
      <div className="min-w-0">
        <p className="m-0 text-class-gloss text-class-ink-faint">
          Zdjęcie na karcie{coverUrl ? '' : ': brak'} ·{' '}
          <label className="cursor-pointer font-semibold text-class-sky-700 hover:text-class-ink has-[:focus-visible]:underline">
            {upload.isPending ? 'Wgrywam…' : coverUrl ? 'Zmień zdjęcie' : 'Dodaj zdjęcie'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={upload.isPending}
              onChange={(e) => {
                pick(e.target.files?.[0])
                e.target.value = ''
              }}
              className="sr-only"
            />
          </label>
          {coverUrl && (
            <>
              {' · '}
              <button type="button" onClick={() => onChange(null)} className="cl-link-quiet">
                Usuń zdjęcie
              </button>
            </>
          )}
        </p>
        <p className="m-0 mt-0.5 text-class-label text-class-ink-faint">Poziomy kadr, np. 1200 × 750. Zapisuje się razem z resztą.</p>
        {error && <p className="m-0 mt-1 text-class-label font-semibold text-class-wrong">✗ {error}</p>}
      </div>
    </div>
  )
}

/* ── drag to reorder ───────────────────────────────────────────────────── */

/**
 * Drag a row by its ⋮⋮ grip, with a mouse or a finger (pointer events, so it
 * works on a tablet too). The list shows the new order while she drags and
 * saves it when she lets go. From the keyboard: focus the grip, ↑ / ↓.
 *
 * The moves are followed on `window`, not on the grip: React moves the
 * dragged row in the DOM as the order changes, and a moved element loses its
 * pointer capture — the rest of the drag would never reach it.
 */
export function useDragOrder(ids: string[], onCommit: (ids: string[]) => void, disabled = false) {
  const [drag, setDrag] = useState<{ id: string; order: string[] } | null>(null)
  const rows = useRef(new Map<string, HTMLElement>())
  const grips = useRef(new Map<string, HTMLButtonElement>())
  const order = drag ? drag.order.filter((id) => ids.includes(id)) : ids

  // The window listeners read the latest values through refs.
  const latest = useRef({ drag, ids, onCommit })
  latest.current = { drag, ids, onCommit }

  function commit(next: string[]) {
    if (next.join() !== latest.current.ids.join()) latest.current.onCommit(next)
  }

  const active = drag?.id ?? null
  useEffect(() => {
    if (!active) return
    const move = (e: globalThis.PointerEvent) => {
      const cur = latest.current.drag
      if (!cur) return
      const others = cur.order.filter((x) => x !== cur.id)
      let index = 0
      for (const other of others) {
        const r = rows.current.get(other)?.getBoundingClientRect()
        if (r && e.clientY > r.top + r.height / 2) index++
      }
      const next = [...others.slice(0, index), cur.id, ...others.slice(index)]
      if (next.join() !== cur.order.join()) setDrag({ id: cur.id, order: next })
    }
    const up = () => {
      const cur = latest.current.drag
      if (cur) commit(cur.order.filter((x) => latest.current.ids.includes(x)))
      setDrag(null)
    }
    const cancel = () => setDrag(null)
    const selectBefore = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', cancel)
    return () => {
      document.body.style.userSelect = selectBefore
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  return {
    order,
    dragging: drag?.id ?? null,
    row: (id: string) => (el: HTMLElement | null) => {
      if (el) rows.current.set(id, el)
      else rows.current.delete(id)
    },
    grip: (id: string, label: string): GripProps => ({
      label,
      disabled,
      active: drag?.id === id,
      gripRef: (el) => {
        if (el) grips.current.set(id, el)
        else grips.current.delete(id)
      },
      onPointerDown: (e) => {
        if (disabled || e.button !== 0) return
        e.preventDefault()
        setDrag({ id, order: ids })
      },
      onKeyDown: (e) => {
        if (disabled || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
        e.preventDefault()
        const i = ids.indexOf(id)
        const j = i + (e.key === 'ArrowUp' ? -1 : 1)
        if (i < 0 || j < 0 || j >= ids.length) return
        const next = [...ids]
        ;[next[i], next[j]] = [next[j]!, next[i]!]
        commit(next)
        // The row moves in the DOM, which drops focus; put it back on the grip.
        requestAnimationFrame(() => grips.current.get(id)?.focus())
      },
    }),
  }
}

type GripProps = {
  label: string
  disabled: boolean
  active: boolean
  gripRef: (el: HTMLButtonElement | null) => void
  onPointerDown: (e: PointerEvent<HTMLButtonElement>) => void
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void
}

export function Grip({ label, disabled, active, gripRef, ...handlers }: GripProps) {
  const style: CSSProperties = { touchAction: 'none', cursor: disabled ? 'default' : active ? 'grabbing' : 'grab' }
  return (
    <button
      type="button"
      ref={gripRef}
      aria-label={`${label} — przeciągnij albo użyj strzałek ↑ ↓`}
      style={style}
      className="flex h-10 w-6 shrink-0 select-none items-center justify-center rounded-md border-0 bg-transparent p-0 font-class-text text-class-gloss tracking-[-2px] text-[#b8c9d8] hover:text-class-ink-faint"
      {...handlers}
    >
      ⋮⋮
    </button>
  )
}

/* ── slug ──────────────────────────────────────────────────────────────── */

const LATIN: Record<string, string> = {
  ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
  а: 'a', б: 'b', в: 'v', г: 'h', ґ: 'g', д: 'd', е: 'e', є: 'ye', ё: 'yo', ж: 'zh', з: 'z', и: 'y', і: 'i',
  ї: 'yi', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
  х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
}

/** "Lekcja w parze" → "lekcja-w-parze"; Cyrillic is transliterated so UA/RU titles get a readable slug too. */
export function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .split('')
    .map((ch) => LATIN[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'pozycja'
}
